// authService.js - Servicio de autenticación basado en AuthController
import apiClient, { handleApiResponse, handleApiError } from './apiClient';

export const authService = {
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/auth/login', {
        email: credentials.email,
        password: credentials.password
      });

      const result = handleApiResponse(response);

      // ✅ Guardar datos con las mismas claves que AuthContext
      if (result.data?.token || result.data?.accessToken) {
        const token = result.data.token || result.data.accessToken;
        localStorage.setItem('authToken', token);

        // ✅ Calcular y guardar fecha de expiración
        const expiresIn = result.data.expiresIn || result.data.expires_in || 86400; // 24 horas por defecto
        const expiresAt = new Date(Date.now() + (expiresIn * 1000)).toISOString();
        localStorage.setItem('tokenExpiry', expiresAt);

        console.log(`✅ Token guardado, expira en: ${new Date(expiresAt).toLocaleString()}`);
      }

      if (result.data?.refreshToken || result.data?.refresh_token) {
        const refreshToken = result.data.refreshToken || result.data.refresh_token;
        localStorage.setItem('refreshToken', refreshToken);
      }

      if (result.data?.tokenType || result.data?.token_type) {
        const tokenType = result.data.tokenType || result.data.token_type;
        localStorage.setItem('tokenType', tokenType);
      }

      if (result.data?.user) {
        localStorage.setItem('userData', JSON.stringify(result.data.user));
        localStorage.setItem('userRole', result.data.user.role || 'Usuario');
        console.log(`✅ Usuario guardado: ${result.data.user.email || result.data.user.username}`);
      }

      return result;
    } catch (error) {
      console.error('❌ Error en login service:', error);
      return handleApiError(error);
    }
  },

  logout: async () => {
    try {
      // ✅ Intentar logout en el servidor primero
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await apiClient.post('/auth/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('✅ Logout exitoso en servidor');
      }

      // ✅ Limpiar localStorage usando función helper
      clearAuthData();

      return { success: true, message: 'Sesión cerrada exitosamente' };
    } catch (error) {
      console.error('❌ Error en logout, limpiando datos localmente:', error);
      // ✅ Limpiar localStorage incluso si hay error en el servidor
      clearAuthData();

      return { success: true, message: 'Sesión cerrada localmente' };
    }
  },

  // ✅ Función para refrescar token
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No hay refresh token disponible');
      }

      const response = await apiClient.post('/auth/refresh', {
        refreshToken: refreshToken
      });

      const result = handleApiResponse(response);

      // ✅ Actualizar tokens
      if (result.data?.token || result.data?.accessToken) {
        const newToken = result.data.token || result.data.accessToken;
        localStorage.setItem('authToken', newToken);

        const expiresIn = result.data.expiresIn || result.data.expires_in || 86400;
        const expiresAt = new Date(Date.now() + (expiresIn * 1000)).toISOString();
        localStorage.setItem('tokenExpiry', expiresAt);

        console.log('✅ Token refrescado exitosamente');
      }

      if (result.data?.refreshToken || result.data?.refresh_token) {
        const newRefreshToken = result.data.refreshToken || result.data.refresh_token;
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      return result;
    } catch (error) {
      console.error('❌ Error refrescando token:', error);
      clearAuthData(); // Limpiar si no se puede refrescar
      throw error;
    }
  },

  // ✅ Verificar si está autenticado
  isAuthenticated: () => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    const tokenExpiry = localStorage.getItem('tokenExpiry');

    if (!token || !userData) {
      return false;
    }

    // ✅ Verificar si el token no ha expirado
    if (tokenExpiry) {
      const now = new Date().getTime();
      const expiryTime = new Date(tokenExpiry).getTime();

      if (now >= expiryTime) {
        console.log('🔒 Token expirado en isAuthenticated');
        return false;
      }
    }

    return true;
  },

  // ✅ Obtener usuario actual
  getCurrentUser: () => {
    try {
      const userStr = localStorage.getItem('userData');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('❌ Error parsing user data:', error);
      return null;
    }
  },

  // ✅ Obtener token
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  // ✅ Obtener rol del usuario
  getUserRole: () => {
    return localStorage.getItem('userRole') || 'Usuario';
  },

  // ✅ Verificar si el token está por expirar
  isTokenExpiring: (minutesBeforeExpiry = 5) => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return false;

    const now = new Date().getTime();
    const expiryTime = new Date(tokenExpiry).getTime();
    const timeUntilExpiry = expiryTime - now;
    const minutesUntilExpiry = timeUntilExpiry / (1000 * 60);

    return minutesUntilExpiry <= minutesBeforeExpiry && minutesUntilExpiry > 0;
  },

  // ✅ Extender sesión
  extendSession: (hoursToExtend = 24) => {
    const currentExpiry = localStorage.getItem('tokenExpiry');
    if (currentExpiry) {
      const newExpiry = new Date(Date.now() + (hoursToExtend * 60 * 60 * 1000)).toISOString();
      localStorage.setItem('tokenExpiry', newExpiry);
      console.log(`✅ Sesión extendida por ${hoursToExtend} horas hasta: ${new Date(newExpiry).toLocaleString()}`);
      return true;
    }
    return false;
  },

  // ✅ Validar token con el servidor
  validateToken: async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return false;

      const response = await apiClient.get('/auth/validate', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return response.status === 200;
    } catch (error) {
      console.error('❌ Token inválido:', error);
      return false;
    }
  }
};

// ✅ Función helper para limpiar datos de autenticación
const clearAuthData = () => {
  const keysToRemove = [
    'authToken',
    'refreshToken',
    'tokenType',
    'userData',
    'userRole',
    'tokenExpiry'
  ];

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  console.log('🧹 Datos de autenticación limpiados');
};

export default authService;